import { useState, useRef, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation } from 'react-router-dom';
import { Container, AlertTitle } from '@chakra-ui/react';
import { IoMdCheckmark } from 'react-icons/io';
import { LuMinus } from 'react-icons/lu';
import { Button, Text, Alert, AlertIcon } from 'components/v4';
import AGGridMain from 'components/tables/AGGrid/AGGridMain';
import UTCTimeCellRenderer from 'components/tables/AGGrid/CustomCells/UTCTimeCellRenderer';
import CopyCellRenderer from 'components/tables/AGGrid/CustomCells/CopyCellRenderer';
import { LoadingIndicator } from '@/components/v4';
import OrganisationStatusCellRenderer from './cellRenderer/OrganisationStatusCellRenderer';
import CompanyStatuses from './utils/statuses';

import CompanyEdit from './v5/CompanyEdit';
import CompanyApprovalModal from './v5/modals/CompanyApprovalModal';
import useGetAllCompanies from './v5/queries/useGetAllCompanies';
import ActionCellRenderer from './cellRenderer/ActionCellRenderer';
import walkthroughIds from './walkthroughIds';

const COMPANY_MODES = {
    MAIN_TABLE: 'MainTable',
    ADD: 'Add',
    EDIT: 'Edit',
    APPROVAL: 'Approval',
    DELETE: 'Delete',
    CONVERT: 'Convert',
    LOADING: 'Loading',
};

const Companies = () => {
    const { currentScope } = useSelector((state) => state.login);
    const { currentWholesaler, currentPartner, currentCompany } = useSelector(
        (state) => state.navigationLists,
    );
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const gridRef = useRef();

    const [mode, setMode] = useState(COMPANY_MODES.MAIN_TABLE);
    const [selectedCompanyId, setSelectedCompanyId] = useState(null);
    const [approvalModalOpen, setApprovalModalOpen] = useState(false);
    const [approvalCompany, setApprovalCompany] = useState(null);

    const { general } = walkthroughIds.companies;

    const [tableData, setTableData] = useState({
        columns: [],
        data: [],
    });

    const { isLoading, isRefetching, isFetching, refetch } =
        useGetAllCompanies(setTableData);

    useEffect(() => {
        setTableData((prev) => ({ ...prev, data: [] }));
    }, [currentScope]);

    const prevScopeRef = useRef(currentScope);
    const prevWholesalerRef = useRef(currentWholesaler);
    const prevPartnerRef = useRef(currentPartner);
    const prevCompanyRef = useRef(currentCompany);

    useEffect(() => {
        const scopeChanged = prevScopeRef.current !== currentScope;
        const wholesalerChanged =
            prevWholesalerRef.current !== currentWholesaler;
        const partnerChanged = prevPartnerRef.current !== currentPartner;
        const companyChanged = prevCompanyRef.current !== currentCompany;

        if (
            scopeChanged ||
            wholesalerChanged ||
            partnerChanged ||
            companyChanged
        ) {
            if (mode !== COMPANY_MODES.MAIN_TABLE) {
                setMode(COMPANY_MODES.MAIN_TABLE);
                setSelectedCompanyId(null);
            }
            prevScopeRef.current = currentScope;
            prevWholesalerRef.current = currentWholesaler;
            prevPartnerRef.current = currentPartner;
            prevCompanyRef.current = currentCompany;
        }
        // React Query will automatically refetch due to queryKey change
    }, [currentScope, currentWholesaler, currentPartner, currentCompany, mode]);

    useEffect(() => {
        if (location.state?.approval && location.state?.companyData) {
            setApprovalCompany(location.state.companyData);
            setApprovalModalOpen(true);
            navigate(location.pathname, { replace: true, state: {} });
        }
    }, [location.state, location.pathname, navigate]);

    const switchMode = useCallback((newMode, companyId = null) => {
        setMode(newMode);
        if (companyId !== null) {
            setSelectedCompanyId(companyId);
        }
    }, []);

    const resetToMainTable = useCallback(() => {
        setMode(COMPANY_MODES.MAIN_TABLE);
        setSelectedCompanyId(null);
        refetch();
    }, [refetch]);

    const handleAdd = useCallback(() => {
        switchMode(COMPANY_MODES.ADD);
    }, [switchMode]);

    const handleEdit = useCallback(
        (params) => {
            setSelectedCompanyId(params.data.id);
            switchMode(COMPANY_MODES.EDIT);
        },
        [switchMode],
    );

    const handleApproval = useCallback((params) => {
        setSelectedCompanyId(params.data.id);
        setApprovalCompany(params.data);
        setApprovalModalOpen(true);
    }, []);

    const handleManage = useCallback(
        (params) => {
            // Update Redux state for current company
            dispatch({
                type: 'CHANGE_CURRENT_COMPANY',
                payload: params.data.id,
            });

            // Navigate based on current path
            const currentPath = location.pathname;
            if (!currentPath.includes('/services')) {
                const currentParentPath = currentPath.split('/')[1];
                navigate(`/${currentParentPath}`);
            } else {
                navigate('/dashboard-services');
            }
        },
        [dispatch, location, navigate],
    );

    const DefaultPlanCellRenderer = useCallback((params) => {
        const planName = (() => {
            if (!params.value) return '';
            if (typeof params.value === 'string') return params.value;
            if (typeof params.value === 'object' && params.value.name) {
                return params.value.name;
            }
            return '';
        })();

        return <CopyCellRenderer value={planName} />;
    }, []);

    const baseColDefs = useMemo(() => {
        const iconYes = <IoMdCheckmark fontSize="large" />;
        const iconNo = <LuMinus fontSize="large" />;

        return [
            {
                field: 'name',
                headerName: 'Company Name',
                flex: 1,
                minWidth: 200,
                sortable: true,
            },
            {
                field: 'entityName',
                headerName: 'Legal Entity Name',
                flex: 1,
                minWidth: 200,
                sortable: true,
                hide: true,
            },
            {
                field: 'status',
                headerName: 'Status',
                width: 150,
                cellRenderer: (params) => {
                    return (
                        <OrganisationStatusCellRenderer
                            value={params.data?.status}
                            statusMessage={params.data?.statusMessage}
                            noCircle={false}
                        />
                    );
                },
                cellStyle: { display: 'flex', alignItems: 'center' },
                sortable: true,
                // The default colDef uses agMultiColumnFilter, which does not
                // pass a top-level filterParams.cellRenderer down to its nested
                // set filter — so the list rendered the raw status numbers.
                // Numbers and Users declare the set filter directly for the
                // same reason (#2736).
                filter: 'agSetColumnFilter',
                valueFormatter: (params) => {
                    return (
                        CompanyStatuses[params?.value]?.displayedName ||
                        'Unknown'
                    );
                },
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const statusLookup = CompanyStatuses[params?.value];
                        const displayName = statusLookup?.displayedName;
                        const additionalInfo =
                            params?.value !== 0 && statusLookup?.toolTip
                                ? ` (${statusLookup.toolTip})`
                                : '';
                        if (!isSelectedAll) {
                            return (
                                <Text isTruncated>
                                    {`${displayName}${additionalInfo}`}
                                </Text>
                            );
                        } else {
                            return '(Select All)';
                        }
                    },
                },
            },
            {
                field: 'isActive',
                headerName: 'Active',
                width: 100,
                cellRenderer: (params) => {
                    return params.value ? iconYes : iconNo;
                },
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const value = params.value ? iconYes : iconNo;
                        const content = isSelectedAll ? '(Select All)' : value;
                        return content;
                    },
                },
            },
            {
                field: 'isTrial',
                headerName: 'On Trial',
                width: 100,
                cellRenderer: (params) => {
                    return params.value ? iconYes : iconNo;
                },
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const value = params.value ? iconYes : iconNo;
                        const content = isSelectedAll ? '(Select All)' : value;
                        return content;
                    },
                },
            },
            {
                field: 'trialHold',
                headerName: 'Trial On Hold',
                width: 120,
                cellRenderer: (params) => {
                    return params.value ? iconYes : iconNo;
                },
                filterParams: {
                    cellRenderer: (params) => {
                        const isSelectedAll = params?.value === '(Select All)';
                        const value = params.value ? iconYes : iconNo;
                        const content = isSelectedAll ? '(Select All)' : value;
                        return content;
                    },
                },
            },
            {
                field: 'initialDomain',
                headerName: 'Initial Domain',
                flex: 1,
                minWidth: 150,
            },
            {
                field: 'lastSuccessfulPair',
                headerName: 'Last Pairing',
                width: 180,
                cellRenderer: UTCTimeCellRenderer,
                hide: true,
            },
            {
                field: 'defaultPlan',
                headerName: 'Default Plan',
                width: 200,
                cellRenderer: DefaultPlanCellRenderer,
                valueGetter: (params) => {
                    return (
                        params.data?.defaultPlan?.name ||
                        params.data?.defaultPlan
                    );
                },
            },
            {
                field: 'serviceCountries',
                headerName: 'Service Countries',
                hide: true,
            },
            {
                field: 'msTenantID',
                headerName: 'Microsoft Tenant ID',
                hide: true,
            },
            {
                field: 'attribute1',
                headerName: 'Attribute 1',
                hide: true,
            },
            {
                field: 'attribute2',
                headerName: 'Attribute 2',
                hide: true,
            },
            {
                field: 'attribute3',
                headerName: 'Attribute 3',
                hide: true,
            },
            {
                field: 'id',
                headerName: 'Company ID',
                hide: true,
            },
            {
                lockPosition: 'right',
                headerName: 'Action',
                maxWidth: 100,
                filter: false,
                suppressMovable: true,
                lockVisible: true,
                suppressColumnsToolPanel: true,
                suppressHeaderMenuButton: true,
                suppressHeaderFilterButton: true,
                enableRowGroup: false,
                resizable: false,
                sortable: false,
                cellStyle: () => ({
                    display: 'flex',
                    alignItems: 'center',
                }),
                cellRenderer: (params) => (
                    <ActionCellRenderer
                        data={params.data}
                        currentScope={currentScope}
                        general={general}
                        handleEdit={handleEdit}
                        handleApproval={handleApproval}
                        handleManage={handleManage}
                    />
                ),
            },
        ];
    }, [
        DefaultPlanCellRenderer,
        currentScope,
        general,
        handleEdit,
        handleApproval,
        handleManage,
    ]);

    const isFetchingData = isLoading || isRefetching || isFetching;

    // Build table data object with columns
    const tableDataWithColumns = useMemo(
        () => ({
            columns: baseColDefs,
            data: tableData.data,
        }),
        [baseColDefs, tableData.data],
    );

    if (!currentPartner) {
        return (
            <Alert status="error">
                <AlertIcon />
                <AlertTitle>No Partner / Wholesaler Selected</AlertTitle>
                <Text>Please select a wholesaler and a partner.</Text>
            </Alert>
        );
    }

    const mainTable = (
        <Container
            as="div"
            maxW="container.xl"
            px={0}
            aria-label="companies main table">
            <Text
                sx={{
                    fontSize: '20',
                    fontWeight: '500',
                    marginBottom: '15px',
                }}>
                Companies
            </Text>
            <AGGridMain
                gridRef={gridRef}
                tableData={tableDataWithColumns}
                isLoading={isFetchingData}
                refetch={refetch}
                overlayNoRowsTemplate="No Companies Found"
                titleName="Companies"
                isSaveButtonEnabled
                extraComponents={
                    <div style={{ paddingLeft: '20px' }}>
                        <Button
                            size="sm"
                            width="120px"
                            onClick={handleAdd}
                            data-walkthroughid={general.ufAddButton}>
                            + Add Company
                        </Button>
                    </div>
                }
            />
        </Container>
    );

    const modalContent = () => {
        switch (mode) {
            case COMPANY_MODES.MAIN_TABLE:
                return mainTable;
            case COMPANY_MODES.ADD:
                return (
                    <CompanyEdit
                        mode={mode}
                        organisationMode="Company"
                        resetToMainTable={resetToMainTable}
                    />
                );
            case COMPANY_MODES.EDIT:
                return (
                    <CompanyEdit
                        mode={mode}
                        organisationMode="Company"
                        resetToMainTable={resetToMainTable}
                        selectedCompanyID={selectedCompanyId}
                    />
                );
            case COMPANY_MODES.LOADING:
                return <LoadingIndicator />;
            default:
                return mainTable;
        }
    };

    return (
        <>
            {modalContent()}
            <CompanyApprovalModal
                isOpen={approvalModalOpen}
                onClose={() => setApprovalModalOpen(false)}
                company={approvalCompany}
            />
        </>
    );
};

export default Companies;
